import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSystemSettings } from "../lib/systemSettings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogIn } from "lucide-react";

/**
 * Login-Screen.
 *
 * - Logo prominent (max-h-72 auf Desktop, max-h-40 auf Mobile), `object-contain`
 *   erhält Seitenverhältnis für runde/quadratische UND querformatige Logos.
 * - Kein separater Slogan-Text („Future of Vending" entfernt) — falls ein
 *   Betreiber einen Slogan will, gehört er direkt in die Logo-Datei.
 * - Fallback: großer Firmenname aus `company_name`.
 */
export default function Login() {
  const { user, login, error } = useAuth();
  const { brandName } = useSystemSettings();
  const { settings } = useSystemSettings();
  const logoUrl = settings?.logo_data_url || null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false) {
    return <Navigate to={user.principal_type === "customer" ? "/portal" : "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res?.ok) {
      navigate(res.user.principal_type === "customer" ? "/portal" : "/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-zinc-950 relative border-r border-zinc-800 items-center justify-center p-10"
           style={{ backgroundImage: "linear-gradient(135deg,#020617,#0f172a)" }}>
        <div className="text-center w-full">
          {logoUrl ? (
            <img src={logoUrl} alt=""
                 data-testid="login-brand-logo"
                 className="mx-auto max-h-72 max-w-[80%] object-contain" />
          ) : (
            <div className="font-black text-cyan-400 tracking-tighter uppercase leading-[0.85] break-words"
                 style={{ letterSpacing: "-0.05em", fontSize: "clamp(3rem, 8vw, 8rem)" }}>
              {brandName}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden text-center mb-2">
            {logoUrl ? (
              <img src={logoUrl} alt=""
                   data-testid="login-brand-logo-mobile"
                   className="mx-auto max-h-40 max-w-[80%] object-contain" />
            ) : (
              <div className="font-black text-cyan-400 tracking-tighter uppercase leading-tight break-words"
                   style={{ letterSpacing: "-0.03em", fontSize: "clamp(2rem, 10vw, 3.5rem)" }}>
                {brandName}
              </div>
            )}
          </div>

          <div>
            <div className="text-2xl font-black uppercase tracking-tight">Anmeldung</div>
            <div className="text-sm text-zinc-500 mt-1">Mitarbeiter, Admin oder Kunde</div>
          </div>

          <div className="space-y-2">
            <Label>E-Mail</Label>
            <Input required type="email" autoComplete="email" data-testid="login-email-input"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Passwort</Label>
              <Link to="/password-reset-request" data-testid="login-forgot-link"
                    className="text-xs text-cyan-400 hover:underline">
                Passwort vergessen?
              </Link>
            </div>
            <Input required type="password" autoComplete="current-password" data-testid="login-password-input"
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          {error && <div data-testid="login-error" className="text-sm text-rose-400">{error}</div>}

          <Button type="submit" disabled={submitting} data-testid="login-submit-btn"
                  className="w-full h-12 rounded-sm">
            <LogIn size={18} className="mr-2" />
            {submitting ? "Anmelden..." : "Anmelden"}
          </Button>

          <div className="text-center text-sm text-zinc-500 pt-4 border-t border-zinc-800">
            Noch kein Konto?{" "}
            <Link to="/register" data-testid="login-register-link"
                  className="text-cyan-400 hover:underline font-semibold">
              Jetzt registrieren
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
